// =============================================================================
// BILLING RAILS — the cross-rail PROCESSING switch, as DATA-IN-DB.
//
// docs/design/revenuecat-rail-2026-07-24.md. His words: "build revcat and the
// stripe integration in parallel and then be able to talk with a switch." This
// is that switch: one row per payment rail, an `active` flag that gates whether
// that rail's WEBHOOK processes deliveries. It NEVER gates the entitlement
// resolver — resolveEntitlementWithGrants stays RC-blind and rail-blind; the
// switch only decides whether a rail's inbound events are applied to our tables.
//
// Mirrors model_throttle_policy.ts / rate_limit_policy.ts byte-for-byte in
// spirit: a singleton-per-key table (one row per `rail`, UNIQUE so a re-seed
// upserts in place), and the Source that reads it FOLDS TO THE CURRENT BEHAVIOR
// on ANY miss (absent row, inactive row, DB error) and NEVER throws —
// availability over strictness, a switch must never be the thing that takes a
// webhook down. "Current behavior" is defined per rail in BillingRailsSource:
// stripe_web ACTIVE (the launch-week rail, R379), revenuecat_store INACTIVE
// (armed only when he flips it on) — so an EMPTY table is exactly today.
//
// Config is DATA, NOT code: turning a rail on/off is a row UPDATE, never a
// deploy. Additive only (rule #11), applied via the Neon tool, mirrored here,
// exported from index.ts. Nothing dropped or renamed.
// =============================================================================

import { sql } from 'drizzle-orm';
import { boolean, index, pgTable, text, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** The rail keys this switch governs. 'stripe_web' is the launch-week direct-
 *  Stripe checkout rail (R379); 'revenuecat_store' is the RevenueCat-fed
 *  Apple/Play (and Stripe-sync) rail. A future rail is just a new row + a new
 *  DEFAULT entry in BillingRailsSource — no shape change — so this union is
 *  documentation, not an enum constraint (matches ThrottlePolicyKey /
 *  RateLimitRouteClass's same posture: the column is a plain varchar). */
export type BillingRail = 'stripe_web' | 'revenuecat_store';

export const billingRails = pgTable(
  'billing_rails',
  {
    id: id(),
    /** The rail key — UNIQUE so a re-seed/flip upserts in place (ON CONFLICT)
     *  instead of duplicating. Read by BillingRailsSource.isActive(rail). */
    rail: varchar('rail', { length: 24 }).notNull(),
    /** Row toggle. An absent row OR active=false is treated by the Source as
     *  "this rail's CURRENT-BEHAVIOR default" (stripe_web on, revenuecat_store
     *  off) — never silently flipped the other way. Defaults true so that
     *  simply inserting a rail row (the common "turn it on" action for RC)
     *  arms it; an explicit active=false is the deliberate "pause this rail." */
    active: boolean('active').notNull().default(true),
    /** Human-readable note on why this rail is set the way it is (optional) —
     *  e.g. "RC armed 2026-07-2x once sandbox purchase verified." */
    notes: text('notes'),
    ...timestamps,
  },
  (t) => ({
    railIdx: index('billing_rails_rail_idx').on(t.rail),
    railUq: uniqueIndex('billing_rails_rail_uq').on(t.rail),
  }),
);

/** The minimal shape a rail-switch reader needs from a `billing_rails` row.
 *  Pick-shaped (like SubscriptionRowLike / EntitlementGrantRowLike) so a
 *  caller/test can pass a real DB row or a plain fixture. */
export interface BillingRailRowLike {
  rail: string;
  active: boolean;
}
